// admin edid or modify user details

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Grid,
  Button,
  TextField,
  MenuItem,
  Select,
  SelectChangeEvent,
  Menu,
} from '@mui/material';
import axios from 'axios';
import { BASEURL, ARROW, ConfirmationDialog } from '../../../constants/Common';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import MoreVertIcon from '@mui/icons-material/MoreVert';

interface UserData {
  organization_name: string;
  user_role: string;
  username: string;
  email: string;
  employee_id: string;
  user_category: string;
  date_joined: string;
  user_title: string;
}

const AdminEditOrModifyUserDetails = () => {
  const [user, setUser] = useState<UserData | null>({
    organization_name: '',
    user_role: '',
    username: '',
    email: '',
    employee_id: '',
    user_category: '',
    user_title: '',
    date_joined: '',
  });
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [roleOptions, setRoleOptions] = useState<string[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const CATEGORY_CHOICES = ['Employee', 'Contractor'];
  const storedData = localStorage.getItem('orgid');
  const viewDirectory = localStorage.getItem('directoryViewId');
  const [openDialog, setOpenDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  let parsedData: any = null;

  if (storedData) {
    parsedData = JSON.parse(storedData);
  } else {
    navigate('/login')
  }

  const userIdrole = viewDirectory;
  const userId = location.state?.userId;

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const response = await axios.get(
          BASEURL + `/user-detailadduser/${userIdrole}`
        );
        setUser(response.data.user);
      } catch (error) {
        console.error('Error fetching user details:', error);
      }
    };

    fetchUserDetails();
  }, [userId]);

  const handleSave = async () => {
    setIsSaving(true)
    try {
      // console.log('user:', user);
      if (!user) return;

      const {
        employee_id,
        username,
        organization_name,
        email,
        user_category,
        user_role,
        user_title,
      } = user;
      const payload = {
        employee_id,
        organization_name,
        username,
        email,
        user_category,
        user_role,
        user_title,
      };

      const response = await axios.put(
        BASEURL + `/user-detailadduser/${userIdrole}/`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      navigate('/directory');
      toast.success('Updated user successfully');
    } catch (error) {
      setIsSaving(false)
      console.error('Error updating user details:', error);
      toast.error('Failed to updated user');
    }finally {
      setIsSaving(false); // Ensure the button is re-enabled after the save operation completes
    }
  };

  const organizationid = parsedData;

  //fetch username
  useEffect(() => {
    const fetchUserName = async () => {
      try {
        const response = await axios.get(
          BASEURL + `/userrole/by-organization/${organizationid}/`
        );
        const roleData = response.data;
        setRoleOptions(roleData);
      } catch (error) {
        console.error('Error fetching current date:', error);
      }
    };

    fetchUserName();
  }, []);

  const handleCancel = () => {
    // Reset user data or fetch again from API to discard changes
    navigate('/directory');
  };

  const handleDialogOpen = () => {
    setAnchorEl(null);
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleDeleteClick = async () => {
    setOpenDialog(false);
    try {
      const response = await axios.delete(
        BASEURL + `/delete-user/${userIdrole}/`
      );
      if (response.data.success) {
        toast.success('User deleted successfully');
        // Optionally, you can navigate to a different page after successful deletion
        // history.push('/users');
        navigate('/directory');
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    }
  };

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<string>
  ) => {
    const { name, value } = e.target as HTMLInputElement | HTMLTextAreaElement;
    setUser((prevUser) => ({
      ...(prevUser as UserData),
      [name]: value,
    }));
  };

  return (
    <Box sx={{ mt: '-70px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant='h6' component='div'>
          <Link to={'/useraccount'}>User Account</Link>
          {ARROW} Edit Account Details
        </Typography>
        <Box sx={{ marginLeft: 'auto' }}>
          <IconButton
            aria-controls='simple-menu'
            aria-haspopup='true'
            onClick={(event) => setAnchorEl(event.currentTarget)}
            sx={{ marginRight: '30px' }}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            id='simple-menu'
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
          >
            <MenuItem onClick={handleDialogOpen}>Delete</MenuItem>
          </Menu>
        </Box>
        <ConfirmationDialog
          open={openDialog}
          handleClose={handleDialogClose}
          handleConfirm={handleDeleteClick}
          message="Are you sure you wanted to delete this user? This action is irreversible."
          title="Confirm Deletion"
        />
      </Box>
      <Grid container spacing={2}>
        {user && (
          <>
            <Grid item xs={4}>
              <Typography variant='subtitle1'>Employee ID:</Typography>
            </Grid>
            <Grid item xs={8}>
              <TextField
                variant='outlined'
                name='employee_id'
                value={user.employee_id}
                onChange={handleChange}
                fullWidth
                sx={{
                  width: '350px', // Adjust the width based on edit mode
                  maxWidth: '350px', // Set maximum width based on edit mode
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <Typography variant='subtitle1'>Username:</Typography>
            </Grid>
            <Grid item xs={8}>
              <TextField
                variant='outlined'
                name='username'
                value={user.username}
                onChange={handleChange}
                fullWidth
                sx={{
                  width: '350px', // Adjust the width based on edit mode
                  maxWidth: '350px', // Set maximum width based on edit mode
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <Typography variant='subtitle1'>Organization Name:</Typography>
            </Grid>
            <Grid item xs={8}>
              <TextField
                variant='outlined'
                name='organization_name'
                value={user.organization_name}
                onChange={handleChange}
                InputProps={{ readOnly: true }}
                fullWidth
                sx={{
                  width: '350px', // Adjust the width based on edit mode
                  maxWidth: '350px', // Set maximum width based on edit mode
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <Typography variant='subtitle1'>Email:</Typography>
            </Grid>
            <Grid item xs={8}>
              <TextField
                variant='outlined'
                name='email'
                value={user.email}
                onChange={handleChange}
                fullWidth
                sx={{
                  width: '350px', // Adjust the width based on edit mode
                  maxWidth: '350px', // Set maximum width based on edit mode
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <Typography variant='subtitle1'>Password:</Typography>
            </Grid>
            <Grid item xs={8}>
              <TextField
                variant='outlined'
                fullWidth
                name='password'
                type='password' // Set type to 'password' to display dots
                value={'•••••••'}
                onChange={handleChange}
                sx={{
                  width: '350px', // Adjust the width based on edit mode
                  maxWidth: '350px', // Set maximum width based on edit mode
                }}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={4}>
              <Typography variant='subtitle1'>Created Date:</Typography>
            </Grid>
            <Grid item xs={8}>
              <TextField
                variant='outlined'
                name='date_joined'
                value={user.date_joined}
                onChange={handleChange}
                fullWidth
                sx={{
                  width: '350px', // Adjust the width based on edit mode
                  maxWidth: '350px', // Set maximum width based on edit mode
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <Typography variant='subtitle1'>Category:</Typography>
            </Grid>
            <Grid item xs={8}>
              <Select
                variant='outlined'
                name='user_category'
                value={user.user_category}
                onChange={handleChange}
                fullWidth
                sx={{
                  width: '350px', // Adjust the width based on edit mode
                  maxWidth: '350px', // Set maximum width based on edit mode
                }}
              >
                {CATEGORY_CHOICES.map((category) => (
                  <MenuItem
                    key={category}
                    value={category}
                    selected={category === user?.user_category}
                  >
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={4}>
              <Typography variant='subtitle1'>User Role:</Typography>
            </Grid>
            <Grid item xs={8}>
              <Select
                variant='outlined'
                name='user_role'
                value={user.user_role}
                onChange={handleChange}
                fullWidth
                sx={{
                  width: '350px', // Adjust the width based on edit mode
                  maxWidth: '350px', // Set maximum width based on edit mode
                }}
              >
                {roleOptions.map((role) => (
                  <MenuItem key={role} value={role}>
                    {role}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={4}>
              <Typography variant='subtitle1'>Title:</Typography>
            </Grid>
            <Grid item xs={8}>
              <TextField
                variant='outlined'
                name='user_title'
                value={user.user_title}
                onChange={handleChange}
                fullWidth
                sx={{
                  width: '350px', // Adjust the width based on edit mode
                  maxWidth: '350px', // Set maximum width based on edit mode
                }}
              />
            </Grid>
          </>
        )}
      </Grid>
      <Box mt={2} sx={{ justifyContent: 'flex-end', display: 'flex' }}>
        <Button variant='contained' onClick={handleSave} sx={{'&:disabled': { bgcolor: 'grey.400', color: 'grey.700' } }}
              disabled={isSaving}>
          Save
        </Button>
        <Button
          variant='contained'
          onClick={handleCancel}
          sx={{ marginLeft: 1 }}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default AdminEditOrModifyUserDetails;
